import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { Invitation } from "../invitation.types";

export type ClaimStatusResponse = {
  has_claimed_type: boolean;
};

export type UseCheckInvitationClaimStatusArgs = {
  /** Current user (query disabled when null) */
  user: { id: number } | null;
  /** The invitation to check claim status for (query disabled when null) */
  invitation: Pick<Invitation, "shortcode"> | null;
};

/**
 * Hook to check whether the authenticated user has already claimed an invitation
 * of the same enrollment type for the same program.
 *
 * For participant/participant_friend invitations, checks if the user has claimed
 * any participant-type invitation in this program. For org admin invitations,
 * always returns false (org admin invitations are org-specific).
 *
 * @example
 * ```tsx
 * const { data: claimStatus } = useCheckInvitationClaimStatus({ user, invitation });
 * const isEnrolled = claimStatus?.has_claimed_type === true;
 * ```
 */
export function useCheckInvitationClaimStatus({
  user,
  invitation,
}: UseCheckInvitationClaimStatusArgs) {
  return useQuery<ClaimStatusResponse>({
    queryKey: ["invitationClaimStatus", invitation?.shortcode],
    queryFn: async () => {
      return apiRequest(
        `/invitation/${invitation!.shortcode}/claim-status/`,
        "GET",
      );
    },
    enabled: !!user && !!invitation,
  });
}
